import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import type { ViolationLite } from '@/features/validation-bridge'
import { humanizeRuleId } from './humanize-rule-id'

interface ValidatorAlertProps {
  /** Highest-priority blocking violation, or null when the app passes. */
  violation: ViolationLite | null
  /** Total blocking violations in the report (drives the impact line). */
  blockingCount: number
  onAskCogo: (violation: ViolationLite) => void
  /** Developer persona: append the raw rule id to the secondary line (F15). */
  showTechnicalDetail?: boolean
}

/**
 * Headline alert for the top blocking violation, titled in app terms (the
 * block the user named) rather than file/schema terms. The rule stays as
 * secondary detail — humanized for everyone, raw id appended for the
 * developer persona only. The single action is the working remediation
 * path — Ask coogo — wired by ValidatorPanel through useAskCogoHandler.
 */
export function ValidatorAlert({ violation, blockingCount, onAskCogo, showTechnicalDetail }: ValidatorAlertProps) {
  if (!violation) return null

  const impact = blockingCount === 1
    ? 'This issue blocks the app from passing validation.'
    : `This and ${blockingCount - 1} more issue${blockingCount === 2 ? '' : 's'} block the app from passing validation.`

  return (
    <div className="mb-4">
      <Alert variant="destructive" title={`Issues found in block "${violation.blockName}"`}>
        <div className="text-sm">{violation.message}</div>
        <div className="mt-1 text-xs text-muted-foreground">
          {humanizeRuleId(violation.ruleId)}
          {violation.elementId ? ` · ${violation.elementId}` : ''}
        </div>
        {showTechnicalDetail && (
          <div className="mt-0.5 font-mono text-xs text-muted-foreground" data-testid="validator-alert-rule-id">
            {violation.ruleId}
          </div>
        )}
        <div className="mt-1 text-xs">{impact}</div>
        <div className="mt-2 flex gap-2">
          <Button
            variant="destructive"
            size="sm"
            data-testid="validator-alert-ask-cogo"
            onClick={() => onAskCogo(violation)}
          >
            Review issue
          </Button>
        </div>
      </Alert>
    </div>
  )
}
